/**
 * Utility functions for handling event dates
 * 
 * Event dates are stored as JSON array of timestamps: "[1234567890, 2345678901]"
 * This supports multiple dates while maintaining backward compatibility with single dates.
 */

/**
 * Parse event dates from stored value (JSON array, comma-separated string, or single number)
 * @param {string|number|Array} value - The stored event date value
 * @returns {number[]} Array of timestamps, sorted ascending
 */
export const parseEventDates = value => {
  if (!value) return [];
  
  try {
    // Handle string format
    if (typeof value === 'string') {
      // Try JSON array first (format: "[1234567890, 2345678901]")
      try {
        const parsed = JSON.parse(value);
        
        // Handle double-stringified case where JSON.parse returns a string
        if (typeof parsed === 'string') {
          // Try parsing again
          try {
            const doubleParsed = JSON.parse(parsed);
            if (Array.isArray(doubleParsed)) {
              return doubleParsed
                .filter(v => typeof v === 'number' && v > 0)
                .sort((a, b) => a - b);
            }
          } catch (innerE) {
            // Fall through to comma-separated handling
          }
        }
        
        if (Array.isArray(parsed)) {
          return parsed
            .filter(v => typeof v === 'number' && v > 0)
            .sort((a, b) => a - b);
        }
      } catch (e) {
        // JSON parse failed, try comma-separated format
      }
      
      // Handle comma-separated string format (format: "1234567890,2345678901")
      const timestamps = value
        .split(',')
        .map(str => {
          const num = parseInt(str.trim(), 10);
          return !isNaN(num) && num > 0 ? num : null;
        })
        .filter(num => num !== null)
        .sort((a, b) => a - b);
      
      if (timestamps.length > 0) {
        return timestamps;
      }
    }
  } catch (e) {
    // If parsing fails, fall through to single date handling
  }
  
  // Backward compatibility: handle single numeric timestamp
  if (typeof value === 'number' && value > 0) {
    return [value];
  }
  
  // Already an array
  if (Array.isArray(value)) {
    return value
      .filter(v => typeof v === 'number' && v > 0)
      .sort((a, b) => a - b);
  }
  
  return [];
};

/**
 * Get the earliest (first) event date from the stored value
 * This is used for Sharetribe API queries, which work with a single date field
 * 
 * @param {string|number|Array} value - The stored event date value
 * @returns {number|null} First timestamp or null if no dates
 */
export const getEarliestEventDate = value => {
  const dates = parseEventDates(value);
  return dates.length > 0 ? dates[0] : null;
};

/**
 * Get the latest (last) event date from the stored value
 * Useful for determining if all event dates are in the past
 * 
 * @param {string|number|Array} value - The stored event date value
 * @returns {number|null} Last timestamp or null if no dates
 */
export const getLatestEventDate = value => {
  const dates = parseEventDates(value);
  return dates.length > 0 ? dates[dates.length - 1] : null;
};

/**
 * Check if any event date falls within the given date range
 * This is used for client-side filtering/display logic
 * 
 * @param {string|number|Array} eventDateValue - The stored event date value
 * @param {number} rangeStart - Start of range (timestamp)
 * @param {number} rangeEnd - End of range (timestamp)
 * @returns {boolean} True if any event date falls within the range
 */
export const isEventDateInRange = (eventDateValue, rangeStart, rangeEnd) => {
  const dates = parseEventDates(eventDateValue);
  return dates.some(date => date >= rangeStart && date <= rangeEnd);
};

/**
 * Format event dates for display
 * @param {string|number|Array} value - The stored event date value
 * @param {Object} options - Formatting options
 * @param {string} options.locale - Locale for date formatting (e.g., 'en-US')
 * @param {string} options.format - Date format string (e.g., 'short', 'long')
 * @returns {string} Formatted dates string, comma-separated
 */
export const formatEventDatesForDisplay = (value, options = {}) => {
  const { locale = 'en-US', format = 'medium' } = options;
  const dates = parseEventDates(value);
  
  if (dates.length === 0) return '';
  
  return dates
    .map(timestamp => {
      const date = new Date(timestamp);
      return date.toLocaleDateString(locale, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    })
    .join(', ');
};

/**
 * Generate an array of timestamps for all dates between start and end (inclusive)
 * Useful for adding date ranges to events
 * 
 * @param {Date} startDate - The start date of the range
 * @param {Date} endDate - The end date of the range
 * @returns {number[]} Array of timestamps for each day in the range
 */
export const generateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  
  // Ensure we're working with valid Date objects
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  
  // Validate dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return [];
  }
  
  // Swap if start is after end
  const [actualStart, actualEnd] = start <= end ? [start, end] : [end, start];
  
  // Normalize to start of day (midnight)
  const normalizeDate = date => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };
  
  const startNormalized = normalizeDate(actualStart);
  const endNormalized = normalizeDate(actualEnd);
  
  // Generate array of timestamps
  const timestamps = [];
  const currentDate = new Date(startNormalized);
  
  while (currentDate <= endNormalized) {
    timestamps.push(currentDate.getTime());
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return timestamps;
};
